'use client';
import * as React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import siteConfig from '@/config/site';
import { useAvailability } from '@/hooks/useAvailability';
import type { OpeningHour } from '@/lib/types';

/**
 * Public booking form. Pass the effective opening hours (see resolveHours) so
 * the slots reflect any live override the owner made; falls back to config.
 * `services` is the list the owner manages in the Rendez-vous admin tab
 * (see lib/bookingServices.ts); config/site.ts is only the seed.
 *
 * Note: slot datetimes are composed in the browser's local timezone. Fine when
 * business and visitors share a timezone; make it explicit otherwise.
 */
export default function BookingForm({
  hours = siteConfig.openingHours,
  services = siteConfig.booking.services,
}: {
  hours?: OpeningHour[];
  services?: string[];
}) {
  const openDays = React.useMemo(() => new Set(hours.map((h) => h.day)), [hours]);

  /** Premier jour ouvré à partir d'aujourd'hui — la valeur par défaut du calendrier. */
  const firstOpenDay = React.useMemo(() => {
    const today = dayjs().startOf('day');
    for (let i = 0; i < siteConfig.booking.horizonDays; i++) {
      const d = today.add(i, 'day');
      if (openDays.has(d.day())) return d;
    }
    return null;
  }, [openDays]);

  const [service, setService] = React.useState(services[0] ?? '');
  const [date, setDate] = React.useState(firstOpenDay ? firstOpenDay.format('YYYY-MM-DD') : '');
  const [slot, setSlot] = React.useState('');
  const [form, setForm] = React.useState({ customerName: '', customerEmail: '', customerPhone: '', notes: '' });
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = React.useState<string | null>(null);

  const { takenTimes, loading: loadingSlots } = useAvailability(date);

  React.useEffect(() => setSlot(''), [date]);

  const allSlots = React.useMemo(() => {
    const day = new Date(`${date}T00:00:00`).getDay();
    const step = siteConfig.booking.slotMinutes;
    // A day can have several ranges (split hours); generate slots inside each so
    // the gap between them (e.g. lunch) stays unbookable. Sort + dedupe in case
    // ranges are out of order or overlap.
    const slots = new Set<string>();
    for (const h of hours.filter((x) => x.day === day)) {
      const [oh, om] = h.open.split(':').map(Number);
      const [ch, cm] = h.close.split(':').map(Number);
      for (let m = oh * 60 + om; m + step <= ch * 60 + cm; m += step) {
        slots.add(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
      }
    }
    return [...slots].sort();
  }, [date, hours]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!slot) {
      setError('Veuillez choisir un créneau horaire.');
      return;
    }
    setStatus('submitting');
    const start = new Date(`${date}T${slot}:00`).toISOString();
    const res = await fetch('/api/appointments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, service, start }),
    });
    if (res.ok) {
      setStatus('done');
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error ?? 'Une erreur est survenue. Veuillez réessayer.');
      setStatus('idle');
    }
  }

  if (status === 'done') {
    return (
      <Alert severity="success">
        Merci {form.customerName} ! Votre demande pour <strong>{service}</strong> le{' '}
        {new Date(`${date}T${slot}`).toLocaleString('fr-FR')} a bien été reçue. Nous vous confirmerons très vite.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={submit}>
      <Stack spacing={3}>
        <TextField select label="Prestation" value={service} onChange={(e) => setService(e.target.value)} required>
          {services.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>

        {/* Un vrai calendrier : les jours de fermeture y sont grisés sur place,
            ce qui dit « fermé le week-end » mieux qu'une liste ne le ferait. */}
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
          <DatePicker
            label="Date"
            value={date ? dayjs(date) : null}
            onChange={(d: Dayjs | null) => setDate(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
            minDate={dayjs().startOf('day')}
            maxDate={dayjs().startOf('day').add(siteConfig.booking.horizonDays - 1, 'day')}
            shouldDisableDate={(d: Dayjs) => !openDays.has(d.day())}
            format="dddd D MMMM YYYY"
            slotProps={{
              textField: { required: true, fullWidth: true },
              // Le thème du site n'a aucune ombre : sans filet, le calendrier
              // flotterait sans contour au-dessus de la page.
              desktopPaper: { sx: { border: 1, borderColor: 'divider', borderRadius: '2px' } },
            }}
          />
        </LocalizationProvider>

        <Box>
          <Typography variant="subtitle2" gutterBottom>Créneaux disponibles</Typography>
          {loadingSlots ? (
            <CircularProgress size={24} />
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {allSlots.length === 0 && <Typography color="text.secondary" variant="body2">Fermé ce jour-là.</Typography>}
              {/* Le créneau choisi s'inverse en plein. Les couleurs viennent de
                  la bande qui accueille le formulaire, via deux variables CSS
                  (--slot-selected-bg / -fg) : le formulaire reste utilisable sur
                  n'importe quel fond, clair ou sombre. `aria-pressed` porte
                  l'état pour qui ne voit pas la couleur. */}
              {allSlots.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  variant="outlined"
                  disabled={takenTimes.has(t)}
                  onClick={() => setSlot(t)}
                  aria-pressed={slot === t}
                  sx={
                    slot === t
                      ? (th) => ({
                          bgcolor: `var(--slot-selected-bg, ${th.palette.primary.main})`,
                          color: `var(--slot-selected-fg, ${th.palette.primary.contrastText})`,
                          borderColor: `var(--slot-selected-bg, ${th.palette.primary.main})`,
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: `var(--slot-selected-bg, ${th.palette.primary.main})`,
                          },
                        })
                      : undefined
                  }
                />
              ))}
            </Box>
          )}
        </Box>

        <TextField label="Votre nom" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} required />
        <TextField label="E-mail" type="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} required />
        <TextField label="Téléphone" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} required />
        <TextField label="Remarques (facultatif)" multiline rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

        {error && <Alert severity="error">{error}</Alert>}

        <Button type="submit" variant="contained" size="large" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Envoi…' : 'Demander un rendez-vous'}
        </Button>
      </Stack>
    </Box>
  );
}
